<script>
  // B-core (rules-editor redesign): generalized, reusable category picker —
  // name-based checkboxes (multi) or radios (single), no id typing anywhere.
  // Sister to AllowListPicker (which ALSO picks products, so it stays separate)
  // but shares the same project-resolution path: generation -> template ->
  // project -> /api/projects/:id/product_categories.
  import { createEventDispatcher, onMount } from "svelte";
  import { apiRequest } from "../utils/api";

  export let generationId = null;
  // "single": `selected` is one category id (or null). Emits `change` with a
  //   single id (or null when unchecked).
  // "multi": `selected` is an ordered array of category ids. Emits `change`
  //   with the ordered array. Insertion order is the default; when `orderable`
  //   is set the "Selected order" panel lets the user reorder it (drag or ↑/↓).
  export let mode = "multi"; // "single" | "multi"
  export let selected = null; // single: number|null; multi: number[]
  // B-dnd: when true (and mode === "multi" with a non-empty selection), render
  // an additional "Selected order" panel below the checkbox list that shows
  // the CURRENT order of `selected` and lets the user reorder it — via native
  // HTML5 drag-and-drop (mouse) or ↑/↓ buttons (keyboard/accessible/testable).
  // Only section_order should ever pass this — the other multi rules
  // (no_consecutive/max_count/ratio) are unordered sets.
  export let orderable = false;

  const dispatch = createEventDispatcher();

  let categories = [];
  let loading = true;
  let error = null;
  let projectId = null;

  // Surface load state to the parent form so it can disable Save/Create until
  // the picker has settled. Without this, the container grows from a one-line
  // "Loading…" box to the full checkbox list once the 3 async fetches resolve,
  // shifting the Save button mid-click — a click landing during that settle is
  // swallowed and the save silently never fires. (The min-h/max-h box above
  // already keeps the height stable; this event is the belt-and-braces so a
  // save can't be attempted against an unloaded picker either.)
  $: dispatch("ready", !loading);

  // Drag state for native HTML5 DnD on the order panel.
  let dragIndex = null;
  let dragOverIndex = null;

  onMount(async () => {
    await init();
  });

  async function init() {
    if (!generationId) return;
    loading = true;
    try {
      const gen = await apiRequest(`/api/generations/${generationId}`);
      projectId = gen?.project_template_id;
      if (projectId) {
        const tpl = await apiRequest(`/api/templates/${projectId}`);
        projectId = tpl?.project_id;
      }
      if (projectId) {
        categories = await apiRequest(`/api/projects/${projectId}/product_categories`);
      }
    } catch (e) {
      error = e?.message || "Could not load categories";
    } finally {
      loading = false;
    }
  }

  function isSelected(id) {
    if (mode === "single") return selected === id;
    return Array.isArray(selected) && selected.includes(id);
  }

  function toggle(id) {
    if (mode === "single") {
      selected = id;
      dispatch("change", id);
      return;
    }
    const current = Array.isArray(selected) ? selected : [];
    const idx = current.indexOf(id);
    let next;
    if (idx >= 0) {
      next = [...current];
      next.splice(idx, 1);
    } else {
      // Append preserves insertion order — the order the user checks boxes in
      // becomes the persisted array order (matters for section_order).
      next = [...current, id];
    }
    selected = next;
    dispatch("change", next);
  }

  // Svelte only re-invalidates a markup expression when a variable it
  // DIRECTLY references changes — a plain `categoryName(id)` call in markup
  // does not track `categories` used inside the function body, so once
  // `categories` loads asynchronously (after `selected` is already set from
  // an edit-load), the order panel would keep showing "#id" forever. Building
  // a reactive `id -> name` map keeps `categories` directly referenced by the
  // `$:` block, so it recomputes whenever `categories` changes.
  let categoryNames = {};
  $: categoryNames = Object.fromEntries(categories.map((c) => [ c.id, c.name ]));

  function moveTo(fromIndex, toIndex) {
    const current = Array.isArray(selected) ? selected : [];
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= current.length ||
      toIndex >= current.length
    ) {
      return;
    }
    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    selected = next;
    dispatch("change", next);
  }

  function moveUp(index) {
    moveTo(index, index - 1);
  }

  function moveDown(index) {
    moveTo(index, index + 1);
  }

  // --- Native HTML5 drag-and-drop (mouse). No external library/CDN. ---------

  function onDragStart(event, index) {
    dragIndex = index;
    // Firefox requires data to be set for the drag to initiate at all.
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  }

  function onDragOver(event, index) {
    event.preventDefault(); // required to allow a drop
    event.dataTransfer.dropEffect = "move";
    dragOverIndex = index;
  }

  function onDragLeave(index) {
    if (dragOverIndex === index) dragOverIndex = null;
  }

  function onDrop(event, index) {
    event.preventDefault();
    const from = dragIndex;
    dragIndex = null;
    dragOverIndex = null;
    if (from === null || from === undefined) return;
    moveTo(from, index);
  }

  function onDragEnd() {
    dragIndex = null;
    dragOverIndex = null;
  }
</script>

<div class="border border-neutral-300 rounded-md p-2.5 min-h-[240px] max-h-[240px] overflow-y-auto bg-white text-xs" data-testid="category-picker">
  {#if loading}
    <p class="text-neutral-500 text-[0.76rem]">Loading categories...</p>
  {:else if error}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-[0.76rem]" role="alert">{error}</p>
  {:else if !projectId}
    <p class="text-neutral-500 text-[0.76rem]">No project found for this generation.</p>
  {:else if categories.length === 0}
    <p class="text-neutral-500 text-[0.76rem]">No categories</p>
  {:else}
    {#each categories as cat (cat.id)}
      <label class="flex items-center gap-1.5 py-0.5 cursor-pointer text-[0.78rem] text-neutral-800 hover:text-neutral-950" data-testid="category-picker-option" data-category-id={cat.id}>
        <input
          type={mode === "single" ? "radio" : "checkbox"}
          class="form-checkbox"
          name={mode === "single" ? "category-picker-single" : null}
          checked={isSelected(cat.id)}
          on:change={() => toggle(cat.id)} />
        <span>{cat.name} ({cat.product_count} products)</span>
      </label>
    {/each}
    {#if mode === "multi"}
      <p class="text-[0.72rem] text-neutral-500 mt-1 font-medium">{(selected || []).length} selected</p>
    {/if}
  {/if}
</div>

{#if orderable && mode === "multi" && Array.isArray(selected) && selected.length > 0}
  <!-- B-dnd: shows the CURRENT order of `selected` (not catalog order) — this
       is the only place order is visualized/controlled. Reordering here
       mutates `selected` and dispatches "change" with the reordered array,
       same event contract as the checkbox list above, so RulesPage's
       on:change handler needs no changes. -->
  <div class="border border-neutral-300 rounded-md p-2.5 mt-2 bg-white text-xs" data-testid="category-picker-order">
    <p class="text-[0.72rem] text-neutral-500 mb-1.5 font-semibold uppercase tracking-wide">Selected order</p>
    <ul class="space-y-1">
      {#each selected as id, index (id)}
        <li
          class="flex items-center gap-2 py-1 px-1.5 rounded border text-[0.78rem] text-neutral-800 bg-white transition-colors {dragOverIndex === index && dragIndex !== index ? 'border-brand-500 bg-brand-50' : 'border-neutral-200'}"
          data-testid="category-order-row"
          data-category-id={id}
          draggable="true"
          on:dragstart={(e) => onDragStart(e, index)}
          on:dragover={(e) => onDragOver(e, index)}
          on:dragleave={() => onDragLeave(index)}
          on:drop={(e) => onDrop(e, index)}
          on:dragend={onDragEnd}
        >
          <span class="text-neutral-400 font-mono text-[0.7rem] w-4 text-right select-none" data-testid="category-order-position">{index + 1}</span>
          <span class="flex-1 truncate">{categoryNames[id] || `#${id}`}</span>
          <button
            type="button"
            class="text-neutral-500 hover:text-neutral-900 disabled:opacity-30 disabled:hover:text-neutral-500 px-1"
            data-testid="category-order-up"
            aria-label={`Move ${categoryNames[id] || id} up`}
            disabled={index === 0}
            on:click={() => moveUp(index)}
          >↑</button>
          <button
            type="button"
            class="text-neutral-500 hover:text-neutral-900 disabled:opacity-30 disabled:hover:text-neutral-500 px-1"
            data-testid="category-order-down"
            aria-label={`Move ${categoryNames[id] || id} down`}
            disabled={index === selected.length - 1}
            on:click={() => moveDown(index)}
          >↓</button>
        </li>
      {/each}
    </ul>
  </div>
{/if}
